// XPEN-3746: GL segment rules on lines. The API sets a ruled segment when the line is
// saved and records what it did in `segment_rule`; a value the user picks is marked in
// `segment_overrides` so the rules leave it alone until the line's account changes.

// SurePoint's Facility has its own key; GLOffice keeps its meaning for every other system.
export const GL_FACILITY = 'GLFacility';

const exportFormatOf = (config) => config?.invoice_exporter?.export_format || '';

// Rules apply to SurePoint firms only, as on the API (RULE_EXPORT_FORMATS).
export const supportsSegmentRules = (config) => exportFormatOf(config) === 'surepoint';

// On SurePoint each office posts to one company of the GL string, so a line's office is
// its company. The line's GLOffice decides, else its Office (as the API's office_company);
// a pick writes both so they never disagree. The line's Unit does not count there.
export const officeIsCompany = (config) => supportsSegmentRules(config);

const officeCode = (value) => (value && typeof value === 'object' ? value.value ?? value.code ?? '' : value ?? '');

export const lineOfficeValue = (config, item) =>
    (officeIsCompany(config) ? officeCode(item?.GLOffice) || item?.Office : item?.Office) || null;

export const lineOfficeChange = (config, value) =>
    (officeIsCompany(config) ? { Office: value, GLOffice: value } : { Office: value });

// Segments each accounting system uses, in its order, with the names it gives them; a
// segment without one keeps its usual label. A system absent from this map keeps the
// generic list.
const SEGMENT_CATALOG = {
    surepoint: [
        { key: 'GLUnit' },
        { key: GL_FACILITY, labelKey: 'gl_segment.facility' },
        { key: 'GLNatural' },
    ],
};

export const segmentLabelKey = (config, segment, fallbackKey) =>
    SEGMENT_CATALOG[exportFormatOf(config)]?.find((s) => s.key === segment)?.labelKey || fallbackKey;

// The configuration screen's segments: the system's own, plus any other segment the
// firm has active, so no firm loses a segment it uses.
export const segmentsForConfig = (config, genericSegments) => {
    const catalog = SEGMENT_CATALOG[exportFormatOf(config)];
    if (!catalog) return genericSegments;
    const keys = new Set(catalog.map((s) => s.key));
    const activeOthers = genericSegments.filter(
        (s) => !keys.has(s.key) && config?.gl_account_segment_configuration?.[s.key]?.active === true);
    const usual = (s) => s.labelKey || genericSegments.find((g) => g.key === s.key)?.labelKey;
    return [...catalog.map((s) => ({ ...s, labelKey: usual(s) })), ...activeOthers];
};

export const hasSegmentRules = (data, segment) => {
    if (!supportsSegmentRules(data?.config)) return false;
    const rules = data?.config?.gl_account_segment_configuration?.[segment]?.rules;
    return Array.isArray(rules) && rules.length > 0;
};

export const segmentRuleOf = (item, segment) => item?.segment_rule?.[segment] ?? null;

export const isSegmentOverridden = (item, segment) =>
    Array.isArray(item?.segment_overrides) && item.segment_overrides.includes(segment);

export const withSegmentChoice = (item, segment, value) => {
    const overrides = (item?.segment_overrides || []).filter((s) => s !== segment);
    return { ...item, [segment]: value ?? '', segment_overrides: value ? [...overrides, segment] : overrides };
};

// Hand the segment back to the rules: the next save sets it again.
export const withSegmentRule = (item, segment) => ({
    ...item,
    [segment]: '',
    segment_overrides: (item?.segment_overrides || []).filter((s) => s !== segment),
});

// A line billable to a matter posts to the client cost account; the rules set its
// segment from that account, so the line editors do not offer it.
export const isBillableLine = (item) => [true, 1, '1', 'true'].includes(item?.IsBillable);

// Line tables are `table-layout: fixed`, and a header's col-* class caps its width
// (max-width), so a `width` above the cap does nothing: the Facility header takes
// GL Nat's class to get GL Nat's width. In the card and expense side panels the short
// Tax and Amount columns (and Unit) get these smaller widths when the Facility shows.
export const SIDE_PANEL_COLUMN_STYLES = {
    tax: { width: '10%' },
    amount: { width: '12%' },
    unit: { width: '12%' },
};

// The picker never shrinks below a readable code; the icons beside it do not shrink.
export const FACILITY_PICKER_MIN_WIDTH = '8rem';

// A row picked in the GL account picker, applied to its line. The SurePoint chart carries the
// facility in gl_office while the line's GLOffice is its office (its company), so there the
// row's gl_office sets the Facility as the user's choice where rules govern it, and never
// the office. Elsewhere every picked segment is copied as is.
export const withPickedChartRow = (data, item, segmentValues) => {
    const { GLOffice: chartOffice, ...others } = segmentValues || {};
    if (!officeIsCompany(data?.config)) return { ...item, ...(segmentValues || {}) };
    const picked = { ...item, ...others };
    if (!chartOffice) return picked;
    return hasSegmentRules(data, GL_FACILITY) ? withSegmentChoice(picked, GL_FACILITY, chartOffice) : picked;
};
